import { useMemo, useEffect, useRef, useState } from 'react';
import type { Address } from '../../../../graphql/generated/graphql';
import type { EditorProps } from '../types/editor-props';
import { AddressForm } from '../../../forms/address/AddressForm';
import { useAddressFormState } from '../../../forms/address/hooks';
import { toAddress } from '../../../forms/address/utils';
import { InlineEditorActions } from './InlineEditorActions';
import { useTranslation } from '../../../../providers/UIProvider';

const isAddress = (value: unknown): value is Address => typeof value === 'object' && value !== null;

const ADDRESS_PREFIXES = ['supplierAddress', 'customerAddress'] as const;

/** Inline editor for structured Address values rendered in-place (no modal). */
export function AddressInlineEditor({
  value,
  onChange,
  onCommit,
  onCancel,
  errors,
  onRegisterSave,
  onDraftChange
}: EditorProps) {
  const t = useTranslation('modules.modals.address');
  const addressValue = isAddress(value) ? (value as Address) : null;
  const [saving, setSaving] = useState(false);

  const state = useAddressFormState({ open: true, value: addressValue });

  const fieldPrefix = useMemo(() => {
    for (const p of ADDRESS_PREFIXES) {
      if ((errors ?? []).some(e => e.path?.startsWith(`${p}.`))) return p;
    }
    return undefined;
  }, [errors]);

  const formErrors = useMemo(
    () =>
      (errors ?? []).map(issue => {
        let path = issue.path ?? '';
        for (const p of ADDRESS_PREFIXES) {
          if (path.startsWith(`${p}.`)) {
            path = path.slice(p.length + 1);
            break;
          }
        }
        return { ...issue, path };
      }),
    [errors]
  );

  const handleFieldChange = (field: Parameters<typeof state.setField>[0], val: string) => {
    state.setField(field, val);
    onDraftChange?.();
  };

  const handleSave = async () => {
    setSaving(true);
    state.setSubmitError(null);
    try {
      const next = toAddress(state.form);
      if (onCommit) {
        await onCommit(next);
      } else {
        onChange(next);
      }
    } catch (error) {
      state.setSubmitError(error instanceof Error ? error.message : t('errors.saveError'));
      if (onRegisterSave) throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    state.setSubmitError(null);
    onCancel?.();
  };

  // Register save fn with ScanEditor's global save coordinator when present.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => handleSaveRef.current());
    return () => onRegisterSave(null);
  }, [onRegisterSave]);

  return (
    <div className='flex flex-col gap-sm w-full'>
      <AddressForm
        value={state.form}
        onFieldChange={handleFieldChange}
        autoFocusStreet={false}
        errors={formErrors}
        disabled={saving}
        fieldPrefix={fieldPrefix}
      />
      {!onRegisterSave && (
        <InlineEditorActions t={t} saving={saving} onCancel={handleCancel} onSave={handleSave} />
      )}
    </div>
  );
}

AddressInlineEditor.displayName = 'AddressInlineEditor';
